import { useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { DateInput } from '@/components/ui/date-input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAccounts } from '@/hooks/useAccounts'
import { useCategories, buildPicker } from '@/hooks/useCategories'
import { useSaveRecurring, type RecurrenceFrequency, type TransactionInput } from '@/hooks/useTransactions'
import { useUpdateRecurringRule, type RecurringRule } from '@/hooks/useRecurring'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { enabledCurrencyList, formatCurrency } from '@/lib/currency'
import { useHousehold } from '@/context/HouseholdContext'
import { cn } from '@/lib/utils'
import { accountComboOptions } from '@/lib/accountOptions'
import type { TransactionKind } from '@/types/db'
import { useT, type TFn } from '@/i18n/I18nProvider'
import { useTranslateCategory } from '@/lib/categoryDisplay'

// Schema construído com t() para mensagens de validação traduzidas.
function buildSchema(t: TFn) {
  return z.object({
    kind: z.enum(['income', 'expense']),
    account_id: z.string().uuid(t('rec.form.err.account')),
    category_id: z.string().uuid().nullable(),
    amount: z.number({ message: t('rec.form.err.amountInvalid') }).positive(t('rec.form.err.amountPositive')),
    currency: z.string().min(3),
    description: z.string().optional(),
    frequency: z.enum(['weekly', 'monthly', 'yearly']),
    start_date: z.string().min(1, t('rec.form.err.startDate')),
  })
}
type FormValues = z.infer<ReturnType<typeof buildSchema>>

// A UI oferece presets amigáveis; o banco guarda frequency + interval. Trimestral
// e Semestral são "mensal a cada 3/6 meses" — o cálculo de ocorrências já respeita
// o interval, então não precisa de tipo novo no schema.
type RecurrencePreset = 'weekly' | 'monthly' | 'quarterly' | 'semiannual' | 'yearly'
function presetToRule(p: RecurrencePreset): { frequency: RecurrenceFrequency; interval: number } {
  switch (p) {
    case 'weekly':
      return { frequency: 'weekly', interval: 1 }
    case 'quarterly':
      return { frequency: 'monthly', interval: 3 }
    case 'semiannual':
      return { frequency: 'monthly', interval: 6 }
    case 'yearly':
      return { frequency: 'yearly', interval: 1 }
    default:
      return { frequency: 'monthly', interval: 1 }
  }
}
function ruleToPreset(frequency: RecurrenceFrequency, interval: number): RecurrencePreset {
  if (frequency === 'monthly' && interval === 3) return 'quarterly'
  if (frequency === 'monthly' && interval === 6) return 'semiannual'
  if (frequency === 'weekly') return 'weekly'
  if (frequency === 'yearly') return 'yearly'
  return 'monthly'
}
function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// 0=Dom..6=Sáb, mesma convenção do Date.getDay() e da coluna days_of_week.
const WEEKDAYS: { n: number; key: string }[] = [
  { n: 0, key: 'sun' },
  { n: 1, key: 'mon' },
  { n: 2, key: 'tue' },
  { n: 3, key: 'wed' },
  { n: 4, key: 'thu' },
  { n: 5, key: 'fri' },
  { n: 6, key: 'sat' },
]

export function RecurringFormDialog({
  open,
  onOpenChange,
  rule,
  defaultDate,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  rule?: RecurringRule | null
  /** Data de início ao CRIAR: a tela passa o mês exibido. */
  defaultDate?: string
}) {
  const { t } = useT()
  const tc = useTranslateCategory()
  const { baseCurrency, enabledCurrencies } = useHousehold()
  const { data: accounts } = useAccounts()
  const { data: categories } = useCategories()
  const saveRecurring = useSaveRecurring()
  const updateRule = useUpdateRecurringRule()

  const [mode, setMode] = useState<'indefinite' | 'until' | 'installments'>('indefinite')
  const [endDate, setEndDate] = useState('')
  const [installments, setInstallments] = useState('12')
  // No modo parcelado, o Valor pode ser o de CADA parcela ou o TOTAL (que o app
  // divide igualmente). Padrão 'parcela' = comportamento antigo intacto.
  const [amountBasis, setAmountBasis] = useState<'parcela' | 'total'>('parcela')
  // Aba do corpo (Geral / Recorrência). Volta p/ Geral ao abrir.
  const [section, setSection] = useState<'geral' | 'recorrencia'>('geral')
  // Preset de recorrência (deriva frequency + interval no submit).
  const [preset, setPreset] = useState<RecurrencePreset>('monthly')
  // Semanal em dias escolhidos (0=Dom..6=Sáb). Vazio = 1 dia/semana pela data de
  // início (comportamento clássico). Permite 1 regra cair 2x+ na semana.
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(buildSchema(t)),
    values: {
      kind: (rule?.template.kind as 'income' | 'expense') ?? 'expense',
      account_id: rule?.account_id ?? '',
      category_id: (rule?.template.category_id || null) as string | null,
      amount: rule?.template.amount ?? 0,
      currency: rule?.template.currency ?? baseCurrency,
      description: rule?.template.description ?? '',
      frequency: rule?.frequency ?? 'monthly',
      start_date: rule?.start_date ?? defaultDate ?? todayISO(),
    },
  })

  // Ao (re)abrir, sincroniza o modo parcelado/sem fim a partir da regra.
  useEffect(() => {
    // Descarta edições abandonadas: reset() sem argumento re-aplica os `values`
    // atuais, então reabrir (novo ou edição) sempre começa limpo.
    if (open) reset()
    if (rule) {
      setMode(rule.max_occurrences ? 'installments' : rule.end_date ? 'until' : 'indefinite')
      if (rule.max_occurrences) setInstallments(String(rule.max_occurrences))
      setEndDate(rule.end_date ?? '')
    } else {
      setMode('indefinite')
      setEndDate('')
    }
    // O valor guardado na regra é sempre o DA PARCELA; ao editar volta pra base
    // 'parcela' (não dá pra saber que total o usuário digitou originalmente).
    setAmountBasis('parcela')
    setSection('geral')
    setPreset(ruleToPreset(rule?.frequency ?? 'monthly', rule?.interval ?? 1))
    setDaysOfWeek(rule?.days_of_week ?? [])
  }, [rule, open])

  const kind = watch('kind')
  const accountId = watch('account_id')
  const amountValue = watch('amount')

  // Quantas parcelas e qual o valor de CADA uma, dado o modo do campo Valor.
  // 'total' → divide igualmente (round p/ centavo); 'parcela' → o valor já é o
  // de cada uma. `perParcela` é o que vai para a regra (guarda 1 valor só).
  const installN = mode === 'installments' ? Math.max(1, parseInt(installments || '1', 10)) : 1
  const perParcela =
    mode === 'installments' && amountBasis === 'total'
      ? Math.round(((Number(amountValue) || 0) / installN) * 100) / 100
      : Number(amountValue) || 0
  const totalEfetivo = perParcela * installN // pode diferir do total digitado por centavos

  // Moeda: padrão = a da conta; pode trocar. Ao mudar a conta, volta a seguir.
  const [currency, setCurrency] = useState<string>(rule?.template.currency ?? baseCurrency)
  const prevAccount = useRef<string>('')
  useEffect(() => {
    if (open) {
      const initAcc = rule?.account_id ?? ''
      prevAccount.current = initAcc
      const acc = accounts?.find((a) => a.id === initAcc)
      setCurrency(rule?.template.currency ?? acc?.currency ?? baseCurrency)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  useEffect(() => {
    if (accountId && accountId !== prevAccount.current) {
      prevAccount.current = accountId
      const acc = accounts?.find((a) => a.id === accountId)
      if (acc) setCurrency(acc.currency)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, accounts])

  const tree = buildPicker(categories ?? [], kind)
  const accountOptions: ComboOption[] = accountComboOptions(accounts)
  // Filho carrega o nome do PAI como palavra-chave: digitar "food" traz os filhos.
  const categoryOptions: ComboOption[] = [
    { value: 'none', label: t('rec.form.categoryNone') },
    ...tree.flatMap(({ parent, children }) => [
      { value: parent.id, label: tc(parent.name), bold: true } as ComboOption,
      ...children.map((c): ComboOption => ({ value: c.id, label: tc(c.name), depth: 1, keywords: parent.name })),
    ]),
  ]

  async function onSubmit(values: FormValues) {
    if (mode === 'until' && !endDate) {
      toast.error(t('rec.form.err.endDate'))
      return
    }
    const max = mode === 'installments' ? installN : null
    const endArg = mode === 'until' ? endDate : null
    // A regra guarda SEMPRE o valor da parcela. No modo 'total' foi dividido acima.
    const perOccurrence = mode === 'installments' ? perParcela : values.amount
    // Trimestral/Semestral viram mensal com interval 3/6 — preserva o intervalo
    // real (fixar 1 aqui transformava um trimestral em mensal ao editar).
    const rec = presetToRule(preset)
    // Dias escolhidos só valem no SEMANAL e no modo sem fim (o parcelado
    // materializa por progressão de datas, incompatível com múltiplos dias).
    const daysArg = preset === 'weekly' && mode === 'indefinite' && daysOfWeek.length > 0 ? [...daysOfWeek].sort() : null
    try {
      if (rule) {
        await updateRule.mutateAsync({
          id: rule.id,
          values: {
            account_id: values.account_id,
            category_id: values.category_id || null,
            kind: values.kind,
            amount: perOccurrence,
            currency,
            description: values.description?.trim() || null,
            frequency: rec.frequency,
            interval: rec.interval,
            maxOccurrences: max,
            end_date: endArg,
            daysOfWeek: daysArg,
          },
        })
        toast.success(t('rec.form.toast.updated'))
      } else {
        const base: TransactionInput = {
          account_id: values.account_id,
          category_id: values.category_id || null,
          kind: values.kind,
          amount: perOccurrence,
          currency,
          date: values.start_date,
          description: values.description?.trim() || null,
          status: 'paid',
          exchange_rate: null,
        }
        await saveRecurring.mutateAsync({
          base,
          frequency: rec.frequency,
          interval: rec.interval,
          maxOccurrences: max,
          endDate: endArg,
          daysOfWeek: daysArg,
        })
        toast.success(
          max ? t('rec.form.toast.createdInstallments', { count: max }) : t('rec.form.toast.createdIndefinite'),
        )
      }
      reset()
      onOpenChange(false)
    } catch (e) {
      toast.error(t('rec.form.toast.saveError'), { description: (e as Error).message })
    }
  }

  const saving = saveRecurring.isPending || updateRule.isPending

  const geralFields = (
    <>
      <div className="grid gap-2">
        <Label>{t('rec.form.description')}</Label>
        <Input {...register('description')} placeholder={t('rec.form.descriptionPlaceholder')} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>{t('rec.form.amount')}</Label>
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <MoneyInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
            )}
          />
          {errors.amount && <p className="text-destructive text-xs">{errors.amount.message}</p>}
        </div>
        {/* min-w-0 + w-full: o SelectTrigger é w-fit com whitespace-nowrap, então
            moedas de nome longo (ex.: "GTQ — Quetzal (Guatemala)") não encolhiam e
            transbordavam a coluna, criando barra de rolagem horizontal no miolo. */}
        <div className="grid min-w-0 gap-2">
          <Label>{t('rec.form.currency')}</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="w-full min-w-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {enabledCurrencyList(enabledCurrencies).map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-2">
        <Label>{t('rec.form.account')}</Label>
        <Combobox
          options={accountOptions}
          value={accountId}
          onChange={(v) => setValue('account_id', v, { shouldValidate: true })}
          placeholder={t('rec.form.accountPlaceholder')}
          searchPlaceholder={t('rec.form.accountSearch')}
        />
        {errors.account_id && <p className="text-destructive text-xs">{errors.account_id.message}</p>}
      </div>

      <div className="grid gap-2">
        <Label>{t('rec.form.category')}</Label>
        <Combobox
          options={categoryOptions}
          value={watch('category_id') ?? 'none'}
          onChange={(v) => setValue('category_id', v === 'none' ? null : v)}
          placeholder={t('rec.form.categoryNone')}
          searchPlaceholder={t('rec.form.categorySearch')}
        />
      </div>
    </>
  )

  const recorrenciaFields = (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>{t('rec.form.frequency')}</Label>
          <Select value={preset} onValueChange={(v) => setPreset(v as RecurrencePreset)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">{t('rec.freq.weekly')}</SelectItem>
              <SelectItem value="monthly">{t('rec.freq.monthly')}</SelectItem>
              <SelectItem value="quarterly">{t('rec.freq.quarterly')}</SelectItem>
              <SelectItem value="semiannual">{t('rec.freq.semiannual')}</SelectItem>
              <SelectItem value="yearly">{t('rec.freq.yearly')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>{rule ? t('rec.form.startFixed') : t('rec.form.start')}</Label>
          <DateInput
            value={watch('start_date')}
            onChange={(iso) => setValue('start_date', iso, { shouldValidate: true })}
            disabled={Boolean(rule)}
          />
          {errors.start_date && <p className="text-destructive text-xs">{errors.start_date.message}</p>}
        </div>
      </div>

      {/* Semanal: escolher em quais dias cai. Vazio = 1x/semana no dia do início
          (clássico). Marcar 2+ dias faz UMA regra cair várias vezes na semana. */}
      {preset === 'weekly' && mode === 'indefinite' && (
        <div className="grid gap-2">
          <Label>{t('rec.form.daysOfWeek')}</Label>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((d) => {
              const on = daysOfWeek.includes(d.n)
              return (
                <button
                  key={d.n}
                  type="button"
                  onClick={() =>
                    setDaysOfWeek((prev) =>
                      prev.includes(d.n) ? prev.filter((x) => x !== d.n) : [...prev, d.n].sort(),
                    )
                  }
                  className={cn(
                    'h-8 min-w-11 rounded-md border px-2 text-sm transition-colors',
                    on
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input text-muted-foreground hover:bg-accent',
                  )}
                >
                  {t(`rec.weekday.${d.key}`)}
                </button>
              )
            })}
          </div>
          <p className="text-muted-foreground text-xs">
            {daysOfWeek.length === 0
              ? t('rec.form.daysHintNone')
              : t('rec.form.daysHint', {
                  count: daysOfWeek.length,
                  days: daysOfWeek.map((n) => t(`rec.weekday.${WEEKDAYS[n].key}`)).join(', '),
                })}
          </p>
        </div>
      )}

      <div className="grid gap-3 rounded-lg border p-3">
        <Tabs value={mode} onValueChange={(v) => setMode(v as 'indefinite' | 'until' | 'installments')}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="indefinite">{t('rec.form.mode.indefinite')}</TabsTrigger>
            <TabsTrigger value="until">{t('rec.form.mode.until')}</TabsTrigger>
            <TabsTrigger value="installments">{t('rec.form.mode.installments')}</TabsTrigger>
          </TabsList>
        </Tabs>
        {mode === 'until' && (
          <div className="grid gap-2">
            <Label>{t('rec.form.endDate')}</Label>
            <DateInput value={endDate} onChange={setEndDate} />
          </div>
        )}
        {mode === 'installments' && (
          <div className="grid gap-3">
            <div className="grid gap-2">
              <Label>{t('rec.form.installmentsCount')}</Label>
              <Input type="number" min={1} value={installments} onChange={(e) => setInstallments(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>{t('rec.form.amountBasisLabel')}</Label>
              <Tabs value={amountBasis} onValueChange={(v) => setAmountBasis(v as 'parcela' | 'total')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="parcela">{t('rec.form.amountBasis.each')}</TabsTrigger>
                  <TabsTrigger value="total">{t('rec.form.amountBasis.total')}</TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="text-muted-foreground text-xs">
                {installN} × {formatCurrency(perParcela, currency)} {t(`rec.freq.adverb.${preset}`)} ={' '}
                <span className="font-medium">{formatCurrency(totalEfetivo, currency)}</span>
                {amountBasis === 'total' && Math.abs(totalEfetivo - (Number(amountValue) || 0)) > 0.005 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {' '}
                    {t('rec.form.totalAdjust', { total: formatCurrency(Number(amountValue) || 0, currency) })}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A rolagem fica no miolo, não no DialogContent: assim o menu do Combobox,
          que é portalado aqui dentro, pode transbordar o dialog sem ser cortado. */}
      <DialogContent className="flex max-h-[90svh] flex-col">
        <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader>
            <DialogTitle>{rule ? t('rec.form.title.edit') : t('rec.form.title.new')}</DialogTitle>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto py-4">
            {/* Despesa / Receita — compacto */}
            <Tabs value={kind} onValueChange={(v) => setValue('kind', v as TransactionKind & ('income' | 'expense'))}>
              <TabsList className="grid w-full max-w-[240px] grid-cols-2">
                <TabsTrigger value="expense">{t('rec.form.kind.expense')}</TabsTrigger>
                <TabsTrigger value="income">{t('rec.form.kind.income')}</TabsTrigger>
              </TabsList>
            </Tabs>

            <Tabs value={section} onValueChange={(v) => setSection(v as 'geral' | 'recorrencia')}>
              <TabsList>
                <TabsTrigger value="geral">{t('rec.form.tab.general')}</TabsTrigger>
                <TabsTrigger value="recorrencia">{t('rec.form.tab.recurrence')}</TabsTrigger>
              </TabsList>
            </Tabs>
            {/* Conteúdo montado (hidden) — trocar de aba não perde nada. */}
            <div className={section === 'geral' ? 'grid gap-4' : 'hidden'}>{geralFields}</div>
            <div className={section === 'recorrencia' ? 'grid gap-4' : 'hidden'}>{recorrenciaFields}</div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
